/* file which contains all interfaces that describe our components */
import {
  AddressDTO,
  AddressType,
  ClosuresDetailsDTO,
  ContactDTO,
  CountryDTO,
  CourierDTO,
  CourierServiceDTO,
  DocumentDTO,
  EntityReferenceType,
  MbePackTypeDTO,
  MbeServiceDTO,
  OnlineMbePackTypeDto,
  PaperlessDocumentDto,
  PickupDetailsDTO,
  PickupsDTO,
  SearchInvoiceDTO,
  SearchShipmentDTO,
  ShipmentDTO,
  ShipType,
  zonesChartType
} from "./data.model";
import { TFunction } from "i18next";

export interface BankAccount {
  bankAccountId: number;
  iban: string;
  accountNumber: string;
  abi: string;
  cab: string;
  cin: string;
  bicSwift: string;
  bankName: string;
  description: string;
  owner: string;
}

export interface SetupDropdownItemDTO {
  command: string;
  title: string;
  condition: boolean;
  type?: string;
}

export interface CourierVisibilityDTO {
  courierId: number | string;
  codeAccount: string;
  zipCode: string;
  canRequest: boolean;
  customerId: number | string;
  username: string;
  countryId: string | number;
}

export const enum CourierBusiness {
  MBE = 0, // MBE
  UPS = 1, // UPS
  FEDEX = 2, // FedEx
  TNT = 3, // TNT
  DHL = 4, // DHL
  SDA = 5, // SDA
  SGT = 6, // SGT
  NEXIVE = 7, // Nexive
  GLS = 8, // GLS
  FREE_POSITION = 9, // FREE_POSITION
  CORREOS_EXPRESS = 10, // Correos-Express
  SEUR = 11, // SEUR
  CHRONOPOST = 12, // Chronopost
  DPD = 13, // DPD
  IDS = 14, // IDS
  POST_AG = 15, // Post-AG
  DHL_PAKET = 16, // DHL-Paket
  TIPSA = 17, // TIPSA
  INPOST = 18, // InPost
  HP = 19, // HP
  OTHER = 99, // Other
  USPS = 20, // USPS
  GLS_MICROLOGISTIC = 22, // GLS-Micrologistic
  CTT = 23, // CTT-Express
  PALLETWAYS = 98, // Palletways
  INTIME = 21, // Intime
  LOGIN = 24, // LogIn
  GEL = 96,
  BRT_DPD = 97,
  ROYAL_MAIL = 30,
  WODHLEXPRESS = 50,
  WODHLECOMMERCEBOX = 51,
  WODHLECOMMERCECONS = 54
}

export interface OnlineNewsDTO {
  id?: number;
  title: string;
  description: string;
  validityFrom: string | null;
  validityTo: string | null;
  onlineNewsOrder: number;
  active: boolean;
  iperLink: string;
  author: string;
}

export interface QuickAccessLink {
  name: string;
  svgImage: string;
  clickAction: (...args: any[]) => any;
}

export interface Address {
  id: number;
  address: string;
  type: AddressType;
  province: string;
  city: string;
  countryName: string;
  zipCode: string;
  telephoneContactPersonRef: string;
  emailContactPersonRef: string;
  nameContactPersonRef: string;
  faxContactPersonRef: string;
  entityId?: number;
  entityType?: EntityReferenceType;
}

export interface PriceList {
  priceListMbeId: string;
  name: string;
  creationDate: string;
  lastChangeDate: string;
  shipmentNumber: number;
  clientsNumber: number;
  favourite: boolean;
  selected: boolean;
}

export interface Contact {
  contactMbeId: string;
  firstname: string;
  surname: string;
  category: string;
  selected: boolean;
}

export interface GetContactResults {
  contacts: ContactDTO[];
  searchParams: SearchParams;
}

export interface ContactListItem extends ContactDTO {
  selected: boolean;
}

export interface AddressListItem extends AddressDTO {
  selected: boolean;
}

export interface GetShipmentResults {
  shipments: SearchShipmentDTO[];
  searchParams: SearchParams;
}

export interface GetPaperlessResults {
  content: PaperlessDocumentDto[];
  number: number;
  numberOfElements: number;
  size: number;
  sort?: string;
  totalElements: number;
  totalPages: number;
}

export interface ShipmentListItem extends SearchShipmentDTO {
  selected: boolean;
}

export interface DocumentListItem extends DocumentDTO {
  url: string;
  filename: string;
  selected: boolean;
}

export interface GetInvoiceResults {
  invoices: SearchInvoiceDTO[];
  searchParams: SearchParams;
}

export interface InvoiceListItem extends SearchInvoiceDTO {
  selected: boolean;
}

export interface SearchPaginationButton {
  key: string;
  label: string; //i18n key
  icon: string;
  title?: string; //i18n key
}

export interface SmartChoice {
  smartChoiceName: string;
  smartChoiceDescription: string;
  smartChoiceNotes: string;
  idRangeConfName: number;
  idZonesChartConfName: number;
  idStore: number;
  idMs: number;
  idUser: number;
  smartChoiceEntries: PriceList[];
}

export interface SelectOptionDef {
  label: string;
  value: string | number;
  labelTranslated?: string;
}

export interface SelectedPriceList {
  id: number;
  value: SelectOptionDef;
}

export interface AddressManagementDef {
  address: string;
  type: AddressType;
  province: string;
  city: string;
  subCountryName?: string;
  state: string;
  zipCode: string;
  telephoneContactPersonRef: string;
  emailContactPersonRef: string;
  faxContactPersonRef: string;
}

export interface Oauth2TokenResponse {
  access_token: string;
  token_type: string;
  refresh_token: string;
  expires_in: number;
  scope: string;
  roles: Authority[];
  impersonator: string;
  email: string;
  jti: string;
  tenant: string;
  username: string;
  impersonator_chain?: ImpersonatorChain[];
  impersonator_reason?: string;
}

export interface Authority {
  authority: string;
}

export interface ImpersonatorChain {
  order: number;
  impersonator: string;
  reason?: string;
}

export interface Department {
  departmentId: number;
  tenantId: number;
  departmentBusinessName: string;
  departmentPaymentTerm: string;
  invoiceable: boolean;
  contactPersonRef: number;
  customerMBId: string;
  defaultAssigned: boolean;
  address: Address;
}

export interface FullScreenModalOptions {
  classicon: string;
  title: string | TFunction;
  applyDisabled: boolean;
  applyVisible: boolean;
}

export interface CustomerDetailsDef {
  businessName: string;
  customerType: string;
  fiscalCode: string;
  vatCode: string;
  vatNumber: number;
  mbePrivilege: boolean;
  atecoCode: string;
  eori: string;
  active: boolean;
  bmsId: string;
  printContact: boolean;
  address: string;
  city: string;
  province: string;
  zipCode: string;
  countryName: string;
  nameContactPersonRef: string;
  telephoneContactPersonRef: string;
  emailContactPersonRef: string;
  faxContactPersonRef: string;
  shipmentNote: string;
  shipmentNoteEnable: boolean;
  documentNote: string;
  documentNoteEnable: boolean;
  nameMBEprivilege: string;
  surnameMBEprivilege: string;
  suspendedMBEprivilege: boolean;
  emailMBEprivilege: string;
  groupingCodeMBEprivilege: string;
  pickingAddress: string;
  pickingCity: string;
  pickingProvince: string;
  pickingZipCode: string;
  pickingCountryName: string;
  pickingTelephoneContactPersonRef: string;
  pickingFaxContactPersonRef: string;
  pickingEmailContactPersonRef: string;
  pickingNameContactPersonRef: string;
}

export interface CustomerTableValues {
  customerMbeId: string;
  vat: string;
  businessName: string;
  address: {
    city: string;
    address: string;
    telephoneContactPersonRef: string;
  };
}

export interface CustomerSettingsDef {
  extraCostsUsingMbeRules: boolean;
  extraSize: boolean;
  shipmentMbeNotificationToCustomer: any;
  shipmentMbeNotificationToRecipient: boolean;
  showRealTracking: boolean;
  useCustomerData: boolean;
  active: boolean;
}

export interface ArticlesTableDef {
  articleId?: number;
  description: string;
  occasionalPrice: string;
  active: boolean;
  vatCode: string;
  vatDescription: string;
  cost: string;
  profitDescription: string;
  notes: string;
  shortDescription: string;
  defaultInvoice: boolean;
  type: string;
}

export interface ArticlesDetailsDef {
  articleId?: number;
  description: string;
  occasionalPrice: string;
  active: boolean;
  vatCode: string;
  vatDescription: string;
  cost: string;
  profitDescription: string;
  notes: string;
  shortDescription: string;
  defaultInvoice: boolean;
  type: string;
  stock: boolean;
  unit: string;
  quantity: number;
}

export interface WareHouseArticleDef {
  articleId?: number;
  description: string;
  occasionalPrice: string;
  active: boolean;
  vatCode: string;
  vatDescription: string;
  cost: string;
  profitDescription: string;
  notes: string;
  shortDescription: string;
  defaultInvoice: boolean;
  type: string;
  stock: boolean;
  unit: string;
  quantity: number;
}

export interface UserDef {
  id: number | null;
  firstname: string;
  lastname: string;
  username: string;
  address: string;
  city: string;
  zipCode: string;
  telephone: string;
}

export interface UserStore {
  value: number;
  label: string;
  idEntity: number;
  entityType: string;
}
export interface LegalEntityDef {
  id: number;
  idEntity: number;
  storeName: string;
  descriptionUser: string;
}

export interface Page {
  /** the total number of pages which was derived from (totalElements / size) */
  totalPages: number;
  /** the total number of rows/records */
  totalElements: number;
  /** the sorting parameter for the page */
  sort: any;
  /** the number of rows/records return for the page */
  numberOfElements: number;
  /** set to true if its the last page otherwise false */
  last: boolean;
  /** set to true if it’s the first page otherwise false */
  first: boolean;
  /** the number of records per page; this was passed from the client via param size */
  size: number;
  /** the page number sent by the client */
  number: number;
  /** the data content */
  content: any;
  /** the current page number */
  pageNumber?: number;
  offset?: number;
  pageSize?: number;
}

export interface BankDetailDef {
  id: any;
  abi: any;
  description: any;
  cab: any;
  branch: any;
  businessName: any;
  address: any;
  city: any;
}

export interface CnaeDef {
  cnae_code: string;
  level_1: string[];
  level_2: string[];
  level_3: string[];
  level_4: string[];
  level_5: string[];
}

export interface CustomerPaymentsDef {
  abi: string;
  accountNumber: string;
  active: boolean;
  alwaysShowAlertFaithfulClientCreditLimit: string;
  attachmentType: string;
  authNum: string;
  availableCreditLimit: string;
  bankName: string;
  bicSwift: string;
  billingMethod: string;
  cab: string;
  clientCreditLimit: boolean;
  custConfigurationId: number;
  denomination: string;
  description: string;
  publicAdministration: boolean;
  fixedPaymentDay?: number;
  iban: string;
  ipa: string;
  noInvoice: boolean;
  notePayments: string;
  owner: string;
  paymentType: string;
  printAttachment: boolean;
  printnet: boolean;
  remainingCreditLimit: string;
  sendAttachmentByEmail: boolean;
  sendInvoiceByEmail: boolean;
  showAlertExpiredNotPaidInvoices: string;
  showAlertPassingCreditCardLimit: string;
  showCourierSector: boolean;
  showCourierService: boolean;
  splitPayment: boolean;
  terms: any;
  useStoreSettings: boolean;
  usedCreditLimit: string;
  vatNumber: string;
  emailAddressForInvoice: string;
  enablePrepaidCredit: boolean;
  showSoldOut: boolean;
  mosDoubleCopy: boolean;
  showExpired: boolean;
  mosWithCourierCondition: boolean;
  bankDetailId: string;
  bankId: string;
}

export type ArticleTypes = "generic" | "proforma" | "touchsale";
export type IncotermsType =
  | "EXW"
  | "FCA"
  | "FAS"
  | "FOB"
  | "CFR"
  | "CIF"
  | "CPT"
  | "CIP"
  | "DAF"
  | "DES"
  | "DEQ"
  | "DDU"
  | "DAP"
  | "DDP";

export interface AgentsTableDef {
  id: number | null;
  lastname: string;
  firstname: string;
  telephone: string;
  email: string;
  notes: string;
  type: string;
}

export interface AgentDetailDef {
  id: number;
  lastname: string;
  firstname: string;
  telephone: string;
  email: string;
  notes: string;
  type: string;
  contactId: number;
  emailId: number;
  telephoneId: number;
  entityId: number;
}

export interface DepartmentManagementDef {
  id?: number;
  name: string;
  address: string;
  type: AddressType;
  province: string;
  city: string;
  state: string;
  zipcode: string;
  nameContactPersonRef: string;
  telephoneContactPersonRef: string;
  emailContactPersonRef: string;
  faxContactPersonRef: string;
  departmentAddressId?: number;
  departmentContactAddressId?: number;
  telephoneId?: number;
  emailId?: number;
  faxId?: number;
  selected: boolean;
}

export interface ZonesChartDef {
  idZonesChart: number;
  zonesChartName: string;
  zonesChartDescription: string;
  zonesChartType?: zonesChartType;
  defaultZone?: boolean;
  selected?: boolean;
}

export interface CustomZoneDef {
  idCustomZone?: number;
  idZonesChartConfName: string;
  zoneName: string;
  zoneDescription: string;
  zoneOrder?: number;
}

export interface SubcountryDef {
  countryCode: string;
  idSubcountry: number;
  countryName: string;
  description: string;
  selected: boolean;
}

export interface ZoneElementCustomZoneAssociationDef {
  from: SubcountryDef;
  to: SubcountryDef;
  idMbeZoneElement: number;
  idMbeZone: null | null;
  idMbeService: number | null;
  mbeZoneName: string;
  mbeZoneElementDescription: string | null;
  mbeServiceName: string | null;
  shipmentType: ShipType;
  idSubcountryFrom: number;
  idSubcountryTo: number;
  tenantId?: number;
  selected: boolean;
}

export interface ZoneElementDef {
  idMbeZoneElement: number;
  idMbeZone: null | null;
  idMbeService: number | null;
  mbeZoneName: string;
  mbeZoneElementDescription: string | null;
  mbeServiceName: string | null;
  shipmentType: ShipType;
  idSubcountryFrom: number;
  idSubcountryTo: number;
}

export interface MbeServiceDef {
  idMBEService: number;
  mbeServiceName: string;
  mbeServiceDescription: string | null;
}

export interface CourierServiceDef {
  idCourierService: number;
  courierServiceName: string;
  courierServiceDescription: string | null;
}

export interface AddressDetailManagementDef {
  id?: number;
  address: string;
  type: AddressType;
  province: string;
  city: string;
  state: string;
  zipcode: string;
  nameContactPersonRef: string;
  telephoneContactPersonRef: string;
  emailContactPersonRef: string;
  faxContactPersonRef: string;
  addressAddressId?: number;
  telephoneId?: number;
  emailId?: number;
  faxId?: number;
}

export interface SmartChoiceConsultationInputDef {
  idMBECustomer?: string;
  marginPercentage: number;
  marginOnSurcharges: boolean;
  idSubcountryFrom: number;
  idSubcountryTo: number;
  placeName: string;
  zipCode: string;
  idMBEService: number;
  idMBEPackType: number;
  shipType: ShipType;
  weight: number;
  packageNumber: number;
  packages: SmartChoiceConsultationPackageDef[];
  pickupDate: string;
  smartChoiceConsultationSurchargesList: SmartChoiceConsultationSurchargeDef[];
  idStore: number;
  idMs: number;
  countryCode: string;
}

export interface SmartChoiceConsultationPackageDef {
  sequence: number;
  depth: number;
  width: number;
  height: number;
  customerWeight: number;
  courierWeight: number;
  customerTaxedWeight: number;
  courierTaxedWeight: number;
}

export interface SmartChoiceConsultationSurchargeDef {
  idSurcharge?: number;
  surchargeName: string;
}

export interface VolumetricDivisorDef {
  service: string;
  macroarea: string;
  divisor: number;
  courierName: string;
  type: string;
  idCourier?: number;
  idService?: number;
  idStore: number | null;
  idMultiStore: number | null;
}

export interface CustomerCostsServicesDef {
  id: number;
  tenantId: number;
  description: string;
}

export interface CustomerCostsExtraPricesDef {
  id: number;
  tenantId: number;
  description: string;
  changeable: boolean;
}

export interface AddBanksDef {
  selectedCountry: SelectOptionDef;
  selectedBankId: SelectOptionDef;
  selectedBankDetailId: SelectOptionDef;
}

export interface BanksInformationDef {
  countriesValues: SelectOptionDef[];
  bankValues: SelectOptionDef[];
  bankDetailValues: SelectOptionDef[];
  bankName: string;
  bankId: string;
  bankDetailName: string;
  bankDetailId: string;
}

export interface mbePackageTypeDef {
  idMBEPackType: number;
  packTypeDescription: string;
}

export interface courierDef {
  idCourier: number | null;
  courierName: string;
}

export interface MbeRangeDef {
  idMBERangeConfName: number;
  rangeConfName: string;
  rangeConfDescription: string;
  selected: boolean;
}

export interface ProformaDetailsManagementDef {
  type: number;
  position: number;
  countryOfOrigin: string;
  measureOfMeasurement: string;
  measureOfMeasurementSecondary: string;
  coefficient: number;
  itemsNumber: number;
  itemsTotalValue: any;
  description: string;
  articleId: number;
  itemsSingleValue: any;
  weight?: any;
  customsTariffCode?: string;
  mbeMeasureUnitCode?: string;
  descriptiveRow: boolean;
  hsCode?: string;
  category?: string;
  gender?: string;
  material?: string;
}

export interface ProformaManagementDef {
  invoiceNumber: number;
  invoiceDate: string;
  declarationText: string;
  additionalComment: string;
  currency: string; // CurrencyType; This definition was making it difficult to transform to ProformaDTO
  eori: string;
  totalValue: number;
  proformaDetails: ProformaDetailsManagementDef[];
  incoterms: string;
  vatNumber: string;
  fiscalCode: string;
  receiverVatNumber?: string;
  receiverFiscalCode?: string;
  receiverEori?: string;
  senderCompanyName: string;
  senderAddress: string;
  senderCity: string;
  senderProvince: string;
  senderZipCode: string;
  senderCountryName: string;
  senderPhone: string;
  receiverCompanyName: string;
  receiverSecondAddress: string;
  receiverAddress: string;
  receiverCity: string;
  receiverProvince: string;
  receiverZipCode: string;
  receiverCountryName: string;
  receiverCountryId?: string;
  senderCountryId?: string;
  receiverPhone: string;
  proformaSenderType: string;
  proformaReceiverType: string;
  invoiceType?: string;
  mbeReasonCode?: string;
  declarationStatement?: string;
  vatStatus?: string;
  mrnNumber?: string;
  language?: string;
}

export interface SearchParams {
  search: string; // search string for querying
  orderField: string; // field used to order table
  orderDirection: string; // direction of order
  totalElements: number; // total elements on query
  totalPages: number; // total pages on query (with current page size)
  numberOfElements: number; // number of elements on this page
  page: number; // current page number
  pageSize: number; // current page size,
  tradename?: string; // businessName
  city?: string; // city
  province?: string; //province
  zipCode?: string; //zip_code
  address?: string; //address
  countryId?: string;
  addressType?: string;
  molUsername?: string;
  addressNickname?: string;
  fromNewShipment?: boolean;
}

export interface IPagination {
  currentPage: number;
  perPage: number;
  totalElements: number;
  perPageOptions: number[];
}

export interface ShipmentSearchFilters {
  dateStart: Date | null;
  dateEnd: Date | null;
  mbeService: MbeServiceDTO | null;
  courier: CourierDTO | null;
  courierService: CourierServiceDTO | null;
  packageType: OnlineMbePackTypeDef | null;
  senderCountry: CountryDTO | null;
  receiverCountry: CountryDTO | null;
  MBETrackingNumber: string | null;
  CourierTrackingNumber: string | null;
  reference: string | null;
  shipmentStatus: string[] | null;
  delivered: boolean | null;
  nameReceiver: string | null;
  cityReceiver: string | null;
  addressReceiver: string | null;
  provinceReceiver: string | null;
  ZipReceiver: string | null;
  contactReceiver: string | null;
  notStatuses: string[] | null;
  extraServiceFilters: ExtraServiceFilters[];
  shipmentId: string[] | null;
  shipmentDirection: string | null;
  nameSender?: string | null;
  hasAttachment?: boolean | null;
  sentPaperlessDocument?: boolean | null;
  courierManifest?: boolean | null;
  isEcommerceReturn?: boolean | null;
  onlyFutureDates?: boolean | null;
}

export interface ExtraServiceFilters {
  mbeBusinessId: number;
  value: string | null;
}

export type ShipmentManagementDef = ShipmentDTO;

export interface ShipmentItemDef {
  trackingNumber: string;
  depth: number;
  width: number;
  height: number;
  weight: number;
}

export interface CourierAndCourierService {
  courier: CourierDTO;
  courierService: CourierServiceDTO;
  courierAndCourierServiceName: string;
}

export interface GetClosureResults {
  closures: ClosuresDetailsDTO[];
  searchParams: SearchParams;
}

export interface ClosureListItem extends ClosuresDetailsDTO {
  selected: boolean;
}

export type BannerTypes = "DESKTOP" | "TABLET" | "MOBILE";

export interface GetPickupResults {
  pickups: PickupsDTO;
  searchParams: SearchParams;
}

export interface PickupListItem extends PickupDetailsDTO {
  selected: boolean;
  handle?: boolean;
}

export interface TranslatedCountryDTO extends CountryDTO {
  translatedName: string;
}

export interface TranslatedMbePackageTypeDTO extends MbePackTypeDTO {
  translatedPackTypeDescription: string;
}

export interface OnlineMbePackTypeDef extends OnlineMbePackTypeDto {
  translatedPackTypeDescription: string;
}

export interface Pageable<T> {
  /** the total number of pages which was derived from (totalElements / size) */
  totalPages: number;
  /** the total number of rows/records */
  totalElements: number;
  /** the sorting parameter for the page */
  sort: any;
  /** the number of rows/records return for the page */
  numberOfElements: number;
  /** set to true if its the last page otherwise false */
  last: boolean;
  /** set to true if it’s the first page otherwise false */
  first: boolean;
  /** the number of records per page; this was passed from the client via param size */
  size: number;
  /** the page number sent by the client */
  number: number;
  /** the data content */
  content: T[];
  /** the current page number */
  pageNumber?: number;
  offset?: number;
  pageSize?: number;
}

export interface QuotationCustomDutyItem {
  singleValue: number;
  hsCode: string;
  description: string;
  category: string;
  gender: string;
  material: string;
  weight: number;
  quantity: number;
  isValid?: boolean;
  totalValue: number;
}

export interface ErrorReportingOpts {
  //instana opts
  componentStack?: string;
  meta?: any; // key value - string
}

export interface LegalEntity {
  idEntity: number;
  entityType: string;
  description: string;
  active?: boolean;
  parentId?: number;
  id?: number;
}
export interface DistanceResultDef {
  humanReadable: string;
  inMeters: number;
  hasResult: boolean;
}

/**
 * data derived from current login/ token
 */
export interface CurrentLogin {
  initialized: boolean;
  isItaly: boolean;
  isSpain: boolean;
  isGermany: boolean;
  isAustria: boolean;
  isFrance: boolean;
  isPoland: boolean;
  isPortugal: boolean;
  isSwitzerland: boolean;
  isCroatia: boolean;
}
export interface GdprAssociationsDef {
  vision: GdprValuesDef;
  mktgCenter: GdprValuesDef;
  mktgCorporate: GdprValuesDef;
  profiling: GdprValuesDef;
  cession: GdprValuesDef;
  [propName: string]: any;
}

export interface GdprValuesDef {
  value: number;
  date: Date | string;
  source: SelectOptionDef;
  element: number;
  dateFull?: Date | string;
  idGdprAssociation?: number | string;
  idRefAddress?: number;
  policyVersion?: number;
  name?: string;
}

export interface SelectOptionDef {
  label: string;
  value: string | number;
  labelTranslated?: string;
  idMBEBusiness?: number;
}

export interface EnvDef {
  isLocal: boolean;
  isDevelopment: boolean;
  isIntegration: boolean;
  isProduction: boolean;
  isDev: boolean;
  envLabel: string | undefined;
}

export interface CustomerSelectId {
  customerId: number;
  customerMbeId: string;
  businessName: string;
  defaultStoreId?: number;
  address: {
    address: string;
    province: string;
    country: string;
    zipCode: string;
    city: string;
  };
  micrologistic?: boolean;
}

export interface ColumnDTO {
  id: number;
  data: string;
  readOnly: boolean;
  title: string;
  translatedTitle: string;
  type?: string;
  dateFormat?: string;
  colWidths?: number;
  required?: boolean;
  size?: number;
  group: number;
  disableSelection?: boolean;
  manualRender?: boolean;
  userId?: number;
  renderer?: string;
  source?: [];
  decimals?: number;
  validator?: string;
  value?: string;
  isExtraService: boolean;
  visible?: boolean;
  leftPosition?: string;
  sticky?: boolean;
}

export interface PresetField {
  field: string;
  type: FieldType;
  typeSpec?: string;
  group?: string;
  editable: boolean;
  visible: boolean;
  fallBackField?: string;
  useFallBackField?: string;
}

export type FieldType =
  | "INTEGER"
  | "DECIMAL"
  | "CURRENCY"
  | "CHOOSE_ONE"
  | "CHOOSE_ANY"
  | "CHOOSE_ONE_INTEGER"
  | "CHOOSE_ANY_INTEGER"
  | "INTEGER_LIST"
  | "BOOLEAN"
  | "PRESENCE"
  | "TEXT"
  | "DATE"
  | "DATETIME"
  | "LOCALDATETIME";

export interface GelPickupPointInfos {
  reference: string;
  isReturnOrder: boolean;
  pickupPointId: number;
  code: string;
  description: string;
  address: string;
  city: string;
  zipCode: string;
  department: string;
  country: string;
  lat: number;
  lon: number;
  networkCode: string;
  networkName: string;
  serviceType: string;
  serviceName: string;
  serviceCode: string;
  openingTimes: GelPickupPointDay[];
  shouldUseGELProximityPrices: boolean;
  costPickupPoint: number;
  costZone: number;
  costInsurance: number;
  cost: number;
  costCurrencyCode: string;
  insuranceEnabled: boolean;
  insuranceAccepted: boolean;
  insurancePaidByMerchant: boolean;
}

export interface GelNetworkCodeDTO {
  code: string;
  name: string;
  serviceBusinessId: number;
  courierBusinessId: number;
  tenant?: number;
  active?: boolean;
}

export interface GelPickupPointDay {
  day: number;
  dayDescription: string;
  isClosed: boolean;
  amOpeningTimeFrom: string;
  amOpeningTimeTo: string;
  pmOpeningTimeFrom: string;
  pmOpeningTimeTo: string;
}

export interface WineWarehouseImporterDTO {
  idWineWarehouse: number;
  code: string;
  description: string;
  address: string;
  postcode: string;
  city: string;
  state: string;
  country: string;
  reference: string;
  phone: string;
  email: string;
  active: boolean;
  wineWarehouseCourierCodes: string[];
  countryCode?: string;
  subCountryId?: 0;
  allStates: boolean;
  multistoreId?: number | null;
}

export interface ClaimStatusDTO {
  idClaimStatus: number;
  tenantId: number;
  multistoreId: number | null;
  storeId: number | null;
  description: string;
  creationDate?: string;
  lastUpdateDate?: string;
  incidentStatusActive?: boolean;
  defaultStatus: boolean;
  active: boolean;
}
